import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowFatUp, ArrowLeft, Buildings, Check, CheckCircle, DotsSixVertical, Eye, House, PaperPlaneTilt, PencilSimple, Plus, Trash, Wrench } from "@phosphor-icons/react";
import { RefreshButton } from "@/components/RefreshButton";import { api, adminHeaders, getSecret, clearSecret } from "@/lib/api";
import { InstallAppButton } from "@/components/InstallAppButton";

function SortableRow({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={isDragging ? "opacity-70 relative z-10" : ""}
        >
            {children({ ...attributes, ...listeners })}
        </div>
    );
}

const READ_BASE_MS = 2000;
const READ_PER_WORD_MS = 300;
const READ_MAX_MS = 6000;

const readThresholdMs = (item) => {
    const words = `${item.title || ""} ${item.comment || ""}`.trim().split(/\s+/).filter(Boolean).length;
    return Math.min(READ_MAX_MS, READ_BASE_MS + READ_PER_WORD_MS * words);
};

function TrackableRow({ id, isRead, active, threshold, onRead, readMs, children }) {
    const ref = useRef(null);
    const intervalRef = useRef(null);
    useEffect(() => {
        if (!active || isRead) return undefined;
        const el = ref.current;
        if (!el) return undefined;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (!intervalRef.current) {
                        intervalRef.current = setInterval(() => {
                            readMs.current[id] = (readMs.current[id] || 0) + 250;
                            if (readMs.current[id] >= threshold) {
                                clearInterval(intervalRef.current);
                                intervalRef.current = null;
                                onRead(id);
                            }
                        }, 250);
                    }
                } else if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            },
            { threshold: 0.6 }
        );
        obs.observe(el);
        return () => {
            obs.disconnect();
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [id, isRead, active, threshold, onRead, readMs]);
    return <div ref={ref}>{children}</div>;
}

const URL_SPLIT_RE = /(https?:\/\/[^\s]+)/g;

// render teks: URL otomatis jadi link clickable + wrap supaya URL panjang tidak merusak layout
function Linkified({ text }) {
    return text.split(URL_SPLIT_RE).map((part, i) => {
        if (part.startsWith("http://") || part.startsWith("https://")) {
            // tanda baca di ujung (titik/koma/dll) bukan bagian URL
            const m = part.match(/^(.*?)([.,;!?\)\]]+)?$/s);
            const url = m[1];
            const tail = m[2] || "";
            return (
                <span key={i}>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary underline underline-offset-2 break-all"
                    >
                        {url}
                    </a>
                    {tail}
                </span>
            );
        }
        return <span key={i}>{part}</span>;
    });
}

// render satu baris judul: baris kosong = jarak alinea, "1. teks" -> nomor ter-indent, "- teks" -> bullet point
function TitleLine({ line }) {
    if (!line.trim()) {
        return <span className="block h-3" aria-hidden="true" />;
    }
    const mNum = line.match(/^(\d+)\.\s+(.*)$/);
    if (mNum) {
        return (
            <span className="block pl-5">
                <span className="font-medium">{mNum[1]}.</span> <Linkified text={mNum[2]} />
            </span>
        );
    }
    const mBul = line.match(/^[-\u2022]\s+(.*)$/);
    if (mBul) {
        return (
            <span className="block pl-5">
                {"\u2022"} <Linkified text={mBul[1]} />
            </span>
        );
    }
    return (
        <span className="block">
            <Linkified text={line} />
        </span>
    );
}

const DEPT_INFO = {
    kantor: { name: "Kantor INERRE", Icon: Buildings },
    workshop: { name: "Workshop INERRE", Icon: Wrench },
};

const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
const isToday = (iso) => iso && new Date(iso).toLocaleDateString("id-ID") === new Date().toLocaleDateString("id-ID");

export default function ChecklistPage() {
    const { dept } = useParams();
    const navigate = useNavigate();
    const info = DEPT_INFO[dept];
    const [verified, setVerified] = useState(false);
    const [items, setItems] = useState([]);
    const [waBusy, setWaBusy] = useState(false);
    const [lastReviewed, setLastReviewed] = useState(null);
    const [newTitle, setNewTitle] = useState("");
    const [expandedIds, setExpandedIds] = useState(() => new Set());
    const [showDeleteAll, setShowDeleteAll] = useState(false);
    const [deletePhrase, setDeletePhrase] = useState("");
    const [deletingAll, setDeletingAll] = useState(false);
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [editComment, setEditComment] = useState("");
    const [readIds, setReadIds] = useState(() => new Set());
    const [reachedBottom, setReachedBottom] = useState(false);
    const readMs = useRef({});
    const bottomRef = useRef(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    const handleRead = useCallback((id) => {
        setReadIds((s) => (s.has(id) ? s : new Set(s).add(id)));
    }, []);

    useEffect(() => {
        setReachedBottom(false);
        const el = bottomRef.current;
        if (!el) return undefined;
        const obs = new IntersectionObserver(([e]) => e.isIntersecting && setReachedBottom(true), { threshold: 0 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [items.length]);

    useEffect(() => {
        const saved = getSecret();
        if (!saved) {
            navigate("/admin");
            return;
        }
        api.post("/admin/verify", { phrase: saved })
            .then(() => setVerified(true))
            .catch(() => {
                clearSecret();
                navigate("/admin");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        const res = await api.get(`/admin/checklists/${dept}`, { headers: adminHeaders() });
        setItems(res.data.items);
        setLastReviewed(res.data.last_reviewed_at);
    }, [dept]);

    useEffect(() => {
        if (verified && info) load().catch(() => toast.error("Gagal memuat checklist"));
    }, [verified, info, load]);

    if (!info) return null;
    if (!verified) return null;

    const addItem = async (e) => {
        e.preventDefault();
        const title = newTitle.trim();
        if (!title) return;
        setAdding(true);
        try {
            // seluruh isi textarea = SATU item (Enter tidak memecah); format nomor/bullet dipreserve
            await api.post(`/admin/checklists/${dept}/items`, { title, single: true }, { headers: adminHeaders() });
            setNewTitle("");
            toast.success("Item ditambahkan");
            await load();
        } catch {
            toast.error("Gagal menambah item");
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setEditTitle(item.title);
        setEditComment(item.comment || "");
    };

    const saveEdit = async (item) => {
        if (!editTitle.trim()) {
            toast.error("Judul tidak boleh kosong");
            return;
        }
        try {
            await api.patch(
                `/admin/checklist-items/${item.id}`,
                { title: editTitle, comment: editComment },
                { headers: adminHeaders() }
            );
            toast.success("Item diperbarui");
            setEditingId(null);
            await load();
        } catch {
            toast.error("Gagal memperbarui item");
        }
    };

    const toggleItem = async (item) => {
        try {
            await api.patch(`/admin/checklist-items/${item.id}`, { is_done: !item.is_done }, { headers: adminHeaders() });
            toast.success(!item.is_done ? "Ditandai selesai" : "Diaktifkan kembali");
            await load();
        } catch {
            toast.error("Gagal mengubah status");
        }
    };

    const deleteItem = async (item) => {
        if (!window.confirm(`Hapus item "${item.title}"?`)) return;
        try {
            await api.delete(`/admin/checklist-items/${item.id}`, { headers: adminHeaders() });
            toast.success("Item dihapus");
            await load();
        } catch {
            toast.error("Gagal menghapus item");
        }
    };

    const toggleExpand = (id) =>
        setExpandedIds((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });

    const togglePriority = async (item) => {
        try {
            await api.patch(
                `/admin/checklist-items/${item.id}/priority`,
                { is_priority: !item.is_priority },
                { headers: adminHeaders() }
            );
            toast.success(!item.is_priority ? "Diprioritaskan — pindah ke atas" : "Prioritas dilepas");
            await load();
        } catch {
            toast.error("Gagal mengubah prioritas");
        }
    };

    const deleteAll = async () => {
        setDeletingAll(true);
        try {
            const res = await api.post(
                `/admin/checklists/${dept}/delete-all`,
                { phrase: deletePhrase.trim() },
                { headers: adminHeaders() }
            );
            toast.success(`${res.data.deleted} item dihapus semua`);
            setShowDeleteAll(false);
            setDeletePhrase("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal menghapus semua item");
        } finally {
            setDeletingAll(false);
        }
    };

    const sendWaNotify = async () => {
        if (!window.confirm(`Kirim notifikasi WhatsApp "checklist sudah diperbarui" ke ${dept === "kantor" ? "Admin INERRE" : "Admin Budhi"}?`)) return;
        setWaBusy(true);
        try {
            const res = await api.post(`/admin/wa/notify-checklist`, { dept }, { headers: adminHeaders() });
            toast.success(`Notifikasi terkirim ke ${res.data.to}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal mengirim notifikasi WA");
        } finally {
            setWaBusy(false);
        }
    };

    const toggleReviewed = async () => {
        try {
            if (reviewedToday) {
                await api.delete(`/admin/checklists/${dept}/review`, { headers: adminHeaders() });
                setLastReviewed(null);
                toast.success("Ditandai belum dibaca");
            } else {
                const res = await api.post(`/admin/checklists/${dept}/review`, {}, { headers: adminHeaders() });
                setLastReviewed(res.data.last_reviewed_at);
                toast.success("Ditandai sudah dibaca hari ini");
            }
        } catch {
            toast.error("Gagal mengubah tanda baca");
        }
    };

    const doneItems = items.filter((i) => i.is_done);
    const prioItems = items.filter((i) => !i.is_done && i.is_priority);
    const normItems = items.filter((i) => !i.is_done && !i.is_priority);

    // tracker berurutan dari atas: item hanya "unlocked" bila semua item terbuka di atasnya sudah terbaca
    const unlockedIds = new Set();
    let blocked = false;
    for (const it of [...prioItems, ...normItems]) {
        if (blocked) continue;
        unlockedIds.add(it.id);
        if (!readIds.has(it.id)) blocked = true;
    }

    const onDragEnd = (zone) => (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const zoneList = zone === "prio" ? prioItems : normItems;
        const oldIdx = zoneList.findIndex((i) => i.id === active.id);
        const newIdx = zoneList.findIndex((i) => i.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const newZone = arrayMove(zoneList, oldIdx, newIdx);
        const newPrio = zone === "prio" ? newZone : prioItems;
        const newNorm = zone === "prio" ? normItems : newZone;
        setItems([...doneItems, ...newPrio, ...newNorm]);
        api.post(
            `/admin/checklists/${dept}/reorder`,
            { item_ids: [...newPrio, ...newNorm].map((i) => i.id) },
            { headers: adminHeaders() }
        ).catch(() => {
            toast.error("Gagal menyimpan urutan");
            load();
        });
    };

    const renderRow = (item, dragProps) => {
        const dDone = daysSince(item.done_at);
        const dOpen = daysSince(item.created_at);
        const suggestDelete = item.is_done && dDone !== null && dDone >= 14;
        const staleOpen = !item.is_done && dOpen !== null && dOpen >= 30;
        return (
            <div
                className={`border bg-white p-3 flex items-start gap-2 ${
                    item.is_priority && !item.is_done
                        ? "border-primary"
                        : suggestDelete
                          ? "border-amber-400"
                          : staleOpen
                            ? "border-red-300"
                            : "border-border"
                }`}
                data-testid={`checklist-item-${item.id}`}
            >
                {dragProps && (
                    <button
                        {...dragProps}
                        data-testid={`checklist-drag-${item.id}`}
                        className="cursor-grab touch-none text-muted-foreground hover:text-foreground shrink-0 -ml-1 p-1"
                        title="Geser urutan"
                    >
                        <DotsSixVertical size={16} weight="bold" />
                    </button>
                )}
                <button
                    data-testid={`checklist-toggle-${item.id}`}
                    onClick={() => toggleItem(item)}
                    className="w-10 h-10 -my-2 -ml-1 shrink-0 flex items-center justify-center"
                    aria-label={item.is_done ? "Aktifkan kembali" : "Tandai selesai"}
                >
                    <span
                        className={`w-6 h-6 border flex items-center justify-center transition-colors duration-200 ${
                            item.is_done ? "bg-primary border-primary text-primary-foreground" : "border-border bg-white"
                        }`}
                    >
                        {item.is_done && <Check size={14} weight="bold" />}
                    </span>
                </button>
                {editingId === item.id ? (
                    <div className="flex-1 min-w-0 space-y-2">
                        <textarea
                            data-testid={`checklist-edit-title-${item.id}`}
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            rows={3}
                            className="w-full border border-border px-3 py-1.5 text-sm outline-none focus:border-primary transition-colors bg-transparent resize-y"
                        />
                        <input
                            data-testid={`checklist-edit-comment-${item.id}`}
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                            placeholder="Komentar singkat (opsional)"
                            className="w-full border border-border px-3 py-1.5 text-xs outline-none focus:border-primary transition-colors bg-transparent"
                        />
                        <div className="flex gap-2">
                            <button
                                data-testid={`checklist-edit-save-${item.id}`}
                                onClick={() => saveEdit(item)}
                                className="bg-primary text-primary-foreground px-3 py-1 text-xs font-medium hover:bg-[#9c4a2a] transition-colors"
                            >
                                Simpan
                            </button>
                            <button
                                data-testid={`checklist-edit-cancel-${item.id}`}
                                onClick={() => setEditingId(null)}
                                className="border border-border px-3 py-1 text-xs hover:border-primary transition-colors"
                            >
                                Batal
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 min-w-0">
                        {(() => {
                            const MAX_LINES = 3;
                            const lines = item.title.split("\n");
                            const isLong = lines.length > MAX_LINES;
                            const expanded = expandedIds.has(item.id);
                            const visible = isLong && !expanded ? lines.slice(0, MAX_LINES) : lines;
                            return (
                                <>
                                    <div
                                        className={`text-sm [overflow-wrap:anywhere] ${item.is_done ? "line-through text-muted-foreground" : ""}`}
                                        data-testid={`checklist-title-${item.id}`}
                                    >
                                        {item.ref && <span className="font-mono font-medium">{item.ref} — </span>}
                                        <span>
                                            <Linkified text={visible[0]} />
                                        </span>
                                        {visible.slice(1).map((line, idx) => (
                                            <TitleLine key={idx} line={line} />
                                        ))}
                                    </div>
                                    {isLong && (
                                        <button
                                            data-testid={`checklist-more-${item.id}`}
                                            onClick={() => toggleExpand(item.id)}
                                            className="text-xs text-primary hover:underline mt-0.5"
                                        >
                                            {expanded ? "Sembunyikan" : `Lihat selengkapnya (${lines.length - MAX_LINES} baris lagi)`}
                                        </button>
                                    )}
                                </>
                            );
                        })()}
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                            {item.is_done
                                ? `selesai ${dDone === 0 ? "hari ini" : `${dDone} hari lalu`}`
                                : `dibuat ${dOpen === 0 ? "hari ini" : `${dOpen} hari lalu`}`}
                        </p>
                        {item.comment && (
                            <p className="text-xs text-muted-foreground italic mt-1 [overflow-wrap:anywhere]" data-testid={`checklist-comment-${item.id}`}>
                                <Linkified text={item.comment} />
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5 empty:mt-0">
                            {item.is_priority && !item.is_done && (
                                <span className="text-[10px] font-bold tracking-widest uppercase text-primary border border-primary px-2 py-0.5" data-testid={`checklist-priority-badge-${item.id}`}>
                                    Prioritas
                                </span>
                            )}
                            {trackingActive && !item.is_done && readIds.has(item.id) && (
                                <span className="text-green-700" title="Sudah terbaca" data-testid={`checklist-read-badge-${item.id}`}>
                                    <Eye size={14} weight="fill" />
                                </span>
                            )}
                            {suggestDelete && (
                                <span className="text-[10px] font-bold tracking-widest uppercase text-amber-700 border border-amber-400 px-2 py-0.5" data-testid={`checklist-suggest-delete-${item.id}`}>
                                    Saran: hapus
                                </span>
                            )}
                            {staleOpen && (
                                <span className="text-[10px] font-bold tracking-widest uppercase text-red-700 border border-red-300 px-2 py-0.5" data-testid={`checklist-stale-${item.id}`}>
                                    Terlalu lama terbuka
                                </span>
                            )}
                        </div>
                    </div>
                )}
                <div className="flex flex-col sm:flex-row items-center shrink-0 -my-1 sm:my-0" data-testid={`checklist-actions-${item.id}`}>
                    <button
                        data-testid={`checklist-edit-${item.id}`}
                        onClick={() => startEdit(item)}
                        className="text-muted-foreground hover:text-primary transition-colors p-2"
                        title="Edit item / komentar"
                    >
                        <PencilSimple size={16} weight="light" />
                    </button>
                    {!item.is_done && (
                        <button
                            data-testid={`checklist-priority-${item.id}`}
                            onClick={() => togglePriority(item)}
                            className={`p-2 transition-colors ${
                                item.is_priority ? "text-primary" : "text-muted-foreground hover:text-primary"
                            }`}
                            title={item.is_priority ? "Lepas prioritas" : "Prioritaskan — pindah ke atas"}
                        >
                            <ArrowFatUp size={18} weight={item.is_priority ? "fill" : "light"} />
                        </button>
                    )}
                    <button
                        data-testid={`checklist-delete-${item.id}`}
                        onClick={() => deleteItem(item)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-2"
                        title="Hapus item"
                    >
                        <Trash size={16} weight="light" />
                    </button>
                </div>
            </div>
        );
    };

    const doneCount = items.filter((i) => i.is_done).length;
    const reviewedToday = isToday(lastReviewed);
    const trackingActive = !reviewedToday;
    // hanya item BELUM terceklis yang di-track baca-nya
    const openCount = items.length - doneCount;
    const readCount = items.filter((i) => !i.is_done && readIds.has(i.id)).length;
    const allRead = !trackingActive || openCount === 0 || (readCount === openCount && reachedBottom);
    const { Icon } = info;

    return (
        <div className="min-h-screen" data-testid="checklist-page">
            <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-stone-200">
                <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-4">
                    <button
                        data-testid="checklist-back-btn"
                        onClick={() => navigate("/admin")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Kembali"
                    >
                        <ArrowLeft size={20} weight="light" />
                    </button>
                    <img src="/logo.png" alt="INERRE interior" className="h-8 object-contain" />
                    <div className="min-w-0">
                        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">Checklist Harian</p>
                        <h1 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium leading-tight truncate" data-testid="checklist-dept-name">
                            {info.name}
                        </h1>
                    </div>
                    <Icon size={24} weight="light" className="ml-auto text-primary shrink-0" />
                    <button
                        data-testid="checklist-wa-notify-btn"
                        onClick={sendWaNotify}
                        disabled={waBusy}
                        className="text-muted-foreground hover:text-primary transition-colors shrink-0 disabled:opacity-40"
                        title={`Kirim notifikasi WA ke ${dept === "kantor" ? "Admin INERRE" : "Admin Budhi"}`}
                    >
                        <PaperPlaneTilt size={20} weight="light" />
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                        <RefreshButton />
                        <button
                            data-testid="checklist-home-btn"
                            onClick={() => navigate("/admin")}
                            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                            title="Kembali ke pemindai"
                        >
                            <House size={20} weight="light" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-6 py-8 space-y-6 fade-up">
                <div className="border border-border bg-white p-4 flex flex-wrap items-center gap-3" data-testid="checklist-summary">
                    <p className="text-sm" data-testid="checklist-counts">
                        <span className="font-medium">{doneCount} selesai</span>
                        <span className="text-muted-foreground"> • {items.length - doneCount} belum dikerjakan</span>
                    </p>
                    {trackingActive && (
                        <p className="text-xs text-muted-foreground" data-testid="checklist-read-progress">
                            Terbaca {readCount}/{openCount}
                            {openCount > 0 && (reachedBottom ? " • sudah scroll sampai bawah" : " • belum scroll sampai bawah")}
                        </p>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <span
                            className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 border ${
                                reviewedToday ? "text-green-700 border-green-600" : "text-amber-700 border-amber-500"
                            }`}
                            data-testid="checklist-review-status"
                        >
                            {reviewedToday
                                ? `Sudah dibaca hari ini ${new Date(lastReviewed).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}`
                                : "Belum dibaca hari ini"}
                        </span>
                        <button
                            data-testid="checklist-review-btn"
                            onClick={toggleReviewed}
                            disabled={!reviewedToday && !allRead}
                            title={
                                reviewedToday
                                    ? "Klik untuk mengubah jadi belum dibaca"
                                    : allRead
                                      ? "Tandai sudah dibaca"
                                      : "Baca semua item sampai tuntas dan scroll sampai bawah dulu"
                            }
                            className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1 border disabled:opacity-40 disabled:cursor-not-allowed ${
                                reviewedToday
                                    ? "bg-primary border-primary text-primary-foreground hover:bg-[#9c4a2a]"
                                    : "border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:hover:bg-transparent disabled:hover:text-primary"
                            }`}
                        >
                            {reviewedToday ? (
                                <>
                                    <CheckCircle size={14} weight="fill" /> Sudah Dibaca
                                </>
                            ) : (
                                <>
                                    <Eye size={14} weight="light" /> Tandai Sudah Dibaca
                                </>
                            )}
                        </button>
                    </div>
                    {!allRead && openCount > 0 && (
                        <p className="w-full text-[11px] text-amber-700" data-testid="checklist-review-hint">
                            Tombol aktif setelah semua item benar-benar terbaca (tiap item harus terlihat di layar beberapa detik, makin panjang teksnya makin lama) dan Anda scroll sampai bawah — supaya tidak di-skip.
                        </p>
                    )}
                </div>

                <form onSubmit={addItem} className="border border-border bg-white focus-within:border-primary transition-colors">
                    <div className="flex items-stretch">
                        <textarea
                            data-testid="checklist-add-input"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="Tulis item checklist baru... (boleh beberapa baris — semua jadi SATU item. Baris '1. teks' tampil bernomor, baris '- teks' tampil jadi bullet)"
                            rows={6}
                            className="flex-1 min-w-0 px-4 py-3 text-sm bg-transparent outline-none resize-y"
                        />
                        <button
                            data-testid="checklist-add-btn"
                            type="submit"
                            disabled={adding}
                            className="px-4 bg-primary text-primary-foreground text-sm font-medium hover:bg-[#9c4a2a] transition-colors flex items-center gap-1 disabled:opacity-50"
                        >
                            <Plus size={16} weight="bold" /> Tambah
                        </button>
                    </div>
                </form>

                <div className="space-y-2" data-testid="checklist-items">
                    {items.length === 0 && (
                        <p className="text-sm text-muted-foreground" data-testid="checklist-empty">
                            Belum ada item. Tambahkan item pertama di atas.
                        </p>
                    )}
                    {doneItems.map((item) => (
                        <TrackableRow key={item.id} id={item.id} isRead={readIds.has(item.id)} active={false} threshold={readThresholdMs(item)} onRead={handleRead} readMs={readMs}>
                            {renderRow(item, null)}
                        </TrackableRow>
                    ))}
                    {[
                        { zone: "prio", list: prioItems },
                        { zone: "norm", list: normItems },
                    ].map(
                        ({ zone, list }) =>
                            list.length > 0 && (
                                <DndContext key={zone} sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd(zone)}>
                                    <SortableContext items={list.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                                        <div className="space-y-2" data-testid={`checklist-zone-${zone}`}>
                                            {list.map((item) => (
                                                <TrackableRow key={item.id} id={item.id} isRead={readIds.has(item.id)} active={trackingActive && unlockedIds.has(item.id)} threshold={readThresholdMs(item)} onRead={handleRead} readMs={readMs}>
                                                    <SortableRow id={item.id}>{(props) => renderRow(item, props)}</SortableRow>
                                                </TrackableRow>
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )
                    )}
                    {items.length > 0 && <div ref={bottomRef} data-testid="checklist-bottom-sentinel" className="h-px" />}

                    <div className="pt-4 mt-2 border-t border-border flex flex-wrap items-center gap-2" data-testid="checklist-bottom-actions">
                        <InstallAppButton />
                        {items.length > 0 && !showDeleteAll && (
                            <button
                                data-testid="checklist-delete-all-btn"
                                onClick={() => setShowDeleteAll(true)}
                                className="text-xs text-destructive border border-destructive/40 px-3 py-2 hover:bg-destructive hover:text-white transition-colors flex items-center gap-1"
                            >
                                <Trash size={14} weight="light" /> Hapus Semua Item
                            </button>
                        )}
                    </div>
                    {items.length > 0 && showDeleteAll && (
                        <div data-testid="checklist-delete-all-section">
                            <div className="border border-destructive/50 bg-white p-4 space-y-3 max-w-md">
                                    <p className="text-sm font-medium text-destructive" data-testid="checklist-delete-all-title">
                                        Hapus SEMUA item checklist {info.name}?
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Tindakan ini tidak bisa dibatalkan. Masukkan frasa rahasia admin untuk konfirmasi.
                                    </p>
                                    <input
                                        type="password"
                                        data-testid="checklist-delete-all-phrase"
                                        value={deletePhrase}
                                        onChange={(e) => setDeletePhrase(e.target.value)}
                                        placeholder="Frasa rahasia admin"
                                        className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-destructive"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            data-testid="checklist-delete-all-confirm"
                                            onClick={deleteAll}
                                            disabled={deletingAll || !deletePhrase.trim()}
                                            className="bg-destructive text-white px-3 py-2 text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
                                        >
                                            {deletingAll ? "Menghapus..." : "Ya, Hapus Semua"}
                                        </button>
                                        <button
                                            data-testid="checklist-delete-all-cancel"
                                            onClick={() => {
                                                setShowDeleteAll(false);
                                                setDeletePhrase("");
                                            }}
                                            className="border border-border px-3 py-2 text-xs hover:border-primary transition-colors"
                                        >
                                            Batal
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            </main>
        </div>
    );
}
